import { useContext, useEffect, useState } from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import * as apiCalls from '../../../logic/apiCalls';
import { Spinner } from '../../../components/Spinner';
import log from 'loglevel';
import styles from './ApiDocumentation.module.scss';

/**
 * OpenAPI specification type with info property for type safety.
 */
interface OpenApiSpec {
    info?: {
        description?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

/**
 * API Documentation page that embeds Swagger UI within the React application.
 * This allows users to view the API documentation while using their existing
 * authentication session, avoiding the need to pass tokens in URLs.
 */
export const ApiDocumentation = () => {
    const authenticationContext = useContext(AuthenticationContext);
    const [spec, setSpec] = useState<OpenApiSpec | null>(null);
    const [originalSpec, setOriginalSpec] = useState<OpenApiSpec | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Swagger UI request interceptor that attaches a Bearer token from
     * the AuthenticationContext to every "Try it out" request.
     */
    const requestInterceptor = async (req: Record<string, any>) => {
        try {
            const token = await authenticationContext.getApiTokenAsync();
            if (token?.accessToken) {
                req.headers = {
                    ...req.headers,
                    Authorization: `Bearer ${token.accessToken}`,
                };
                log.debug('[ApiDocumentation] Attached bearer token to request');
            }
        } catch (err) {
            log.error('[ApiDocumentation] Failed to acquire token for request:', err);
        }
        return req;
    };

    useEffect(() => {
        const fetchSpec = async () => {
            try {
                log.debug('[ApiDocumentation] Fetching OpenAPI specification');
                
                // Use the API context to fetch the spec with authentication
                const specData = await apiCalls.getOpenApiSpec(authenticationContext) as OpenApiSpec;
                
                // Store the original spec for download
                setOriginalSpec(specData);
                
                // Remove description from spec to prevent SwaggerUI from rendering it
                // and remove securitySchemes so the Authorize button is not shown
                // (authentication is handled automatically via the request interceptor)
                const specWithNewBearerDescription: OpenApiSpec = {
                    ...specData,
                    info: {
                        ...specData.info,
                        description: undefined,
                    },
                    components: {
                        ...(specData.components as any),
                        securitySchemes: undefined,
                    },
                };
                
                setSpec(specWithNewBearerDescription);
                
                log.debug('[ApiDocumentation] OpenAPI specification loaded successfully');
            } catch (err: any) {
                log.error('[ApiDocumentation] Failed to load API specification:', err);
                setError(err.message || 'Failed to load API documentation');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSpec();
    }, [authenticationContext]);

    const handleDownloadSpec = () => {
        if (!originalSpec) return;
        
        const jsonString = JSON.stringify(originalSpec, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = 'swagger.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
           <Spinner />
        );
    }

    if (error) {
        return (
            <Container fluid>
                <h1>API Documentation</h1>
                <Alert variant="danger" className="mt-3">
                    <Alert.Heading>Failed to load API documentation</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <p className="mb-0">
                        Please try refreshing the page. If the problem persists, contact support.
                    </p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className={`${styles.swaggerContainer} p-0`}>
            <div className={styles.apiDescription}>
                <div className={styles.titleContainer}>
                    <h1>Robin Management API</h1>
                    <img 
                        src="/images/Robin-logo-500-blue.png" 
                        alt="Robin logo" 
                        className={styles.logo}
                    />
                </div>
                <p>
                    The Robin Management API allows you to programmatically manage your Robin resources.
                </p>
            </div>

            <div className={styles.downloadSection}>
                <Button variant="primary" onClick={handleDownloadSpec}>
                    Download Open API Definition
                </Button>
            </div>

            {spec && (
                <SwaggerUI 
                    spec={spec}
                    docExpansion="list"
                    defaultModelsExpandDepth={1}
                    defaultModelExpandDepth={1}
                    displayRequestDuration={true}
                    showExtensions={true}
                    showCommonExtensions={true}
                    requestInterceptor={requestInterceptor}
                />
            )}
        </Container>
    );
};